"use client";
import React, { useEffect, useState } from "react";
import type { CategoryNode } from "@/types/pim";
import { Select } from "antd";
import { useInventoryFilters } from "./InventoryFiltersContext";
import { useIsMobile } from "../hooks/useIsMobile";

interface LocationOption { value: any; count: number; code?: string }

const LOCATION = "Location";

const normalizeSelection = (value: any): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/**
 * Full-width row of Location toggle buttons shown above the inventory grid.
 *
 * Mirrors the sidebar's Location attribute filter: it reads the available
 * Location values (warehouse code / abbreviation / count) from the same
 * /attributes/available endpoint (sharing the in-memory cache with the
 * sidebar) and writes selections to the same "Location" URL param via the
 * shared filter updater, so the two controls stay in sync automatically.
 */
const LocationFilter: React.FC<{
  category: CategoryNode | null;
  filters: Record<string, any>;
  searchQuery?: string;
  onFilterChange: (attributeName: string, value: any) => void;
}> = ({ filters, onFilterChange }) => {
  // Client-side device detection — SSR output stays device-invariant (P3).
  const isMobile = useIsMobile();
  // Available Location values come from the server-computed filter data (no fetch).
  const { availableValues } = useInventoryFilters();
  const options = availableValues[LOCATION] || [];

  // Optimistic selection mirrors filters["Location"] so toggles feel instant
  const [optimistic, setOptimistic] = useState<string[]>(normalizeSelection(filters[LOCATION]));
  useEffect(() => {
    setOptimistic(normalizeSelection(filters[LOCATION]));
  }, [filters]);

  const applySelection = (next: string[]) => {
    setOptimistic(next);
    onFilterChange(LOCATION, next.length > 0 ? next : null);
  };

  const toggleLocation = (code: string) => {
    applySelection(
      optimistic.includes(code)
        ? optimistic.filter((c) => c !== code)
        : [...optimistic, code]
    );
  };

  if (options.length === 0) {
    return null;
  }

  const sorted = [...options].sort((a, b) =>
    String(a.value ?? '').localeCompare(String(b.value ?? ''))
  );

  return (
    <div className="bg-white">
      {/* Desktop: full-width row of toggle buttons */}
      <div className="hidden lg:flex items-center gap-2 flex-wrap px-3 py-1.5">
        {sorted.map((opt) => {
          const code = opt.code ?? String(opt.value);
          const isSelected = optimistic.includes(code);
          return (
            <button
              key={code}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggleLocation(code)}
              className={`hover:cursor-pointer shrink-0 px-4 py-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap ${isSelected
                ? 'bg-gcopper text-white border-gcopper'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                }`}
            >
              {opt.value} ({opt.count ?? 0})
            </button>
          );
        })}
      </div>

      {/* Mobile: multi-select dropdown, matching the sidebar's Location filter UI */}
      <div className="lg:hidden px-3 py-1.5">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
        <Select
          mode="multiple"
          className="w-full"
          placeholder="Select locations"
          showSearch={!isMobile}
          value={optimistic}
          onChange={(values: string[]) => applySelection(values)}
          maxTagCount="responsive"
          options={sorted.map((opt) => ({
            label: `${opt.value} (${opt.count ?? 0})`,
            value: opt.code ?? String(opt.value),
          }))}
          tagRender={(props) => {
            const { label, closable, onClose } = props;
            const displayLabel = typeof label === 'string' ? label.split(' (')[0] : label;
            return (
              <span className="ant-select-selection-item" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span className="ant-select-selection-item-content">{displayLabel}</span>
                {closable && (
                  <span
                    className="ant-select-selection-item-remove"
                    onClick={onClose}
                    style={{ marginLeft: '4px', cursor: 'pointer' }}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          }}
        />
      </div>
    </div>
  );
};

export default LocationFilter;
